import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useSocket } from '../../contexts/SocketContext';
import {
  LayoutDashboard,
  Monitor,
  Settings,
  LogOut,
  User,
  Activity,
  ChevronRight,
  Sun,
  Moon,
  Wifi,
  WifiOff,
  Bell
} from 'lucide-react';
import toast from 'react-hot-toast';

const Sidebar = ({ onClose }) => {
  const { logout, user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { connected } = useSocket();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const navItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/monitors', icon: Monitor, label: 'Your Sites' },
    { to: '/settings', icon: Settings, label: 'Settings' },
  ];

  return (
    <div className="flex flex-col h-full bg-white dark:bg-surface-900 border-r border-surface-200 dark:border-surface-800 shadow-premium transition-colors duration-500">
      {/* Brand Logo Section */}
      <div className="p-8 border-b border-surface-100 dark:border-surface-800/50">
        <div className="flex items-center space-x-4">
          <div className="p-2.5 bg-brand-600 rounded-2xl shadow-glow-brand animate-float">
            <Activity className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-surface-900 dark:text-white leading-none mb-1">
              Monitor
            </h1>
            <div className="flex items-center space-x-1.5">
              <span className={`h-1.5 w-1.5 rounded-full ${connected ? 'bg-success animate-pulse' : 'bg-surface-400'}`}></span>
              <span className="text-[10px] font-bold uppercase tracking-widest text-surface-500 dark:text-surface-500">
                {connected ? 'Live Sync' : 'Reconnecting'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Links */}
      <nav className="flex-1 px-4 py-8 space-y-2 overflow-y-auto scrollbar-hide">
        <div className="px-4 mb-4">
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-surface-400 dark:text-surface-600">
            Main Menu
          </span>
        </div>
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={onClose}
            className={({ isActive }) =>
              `group flex items-center justify-between px-4 py-3.5 rounded-2xl transition-all duration-300 ${isActive
                ? 'bg-brand-600 text-white shadow-premium shadow-brand-500/20'
                : 'text-surface-600 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-800 hover:text-surface-900 dark:hover:text-white'
              }`
            }
          >
            {({ isActive }) => (
              <>
                <div className="flex items-center space-x-4">
                  <item.icon className={`h-5 w-5 transition-transform duration-300 ${isActive ? 'scale-110' : 'group-hover:scale-110'}`} />
                  <span className="font-semibold text-sm tracking-tight">{item.label}</span>
                </div>
                {isActive && (
                  <ChevronRight className="h-4 w-4 opacity-70" />
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Bottom Sticky Section */}
      <div className="p-6 border-t border-surface-100 dark:border-surface-800/50 bg-surface-50/50 dark:bg-surface-900/50 backdrop-blur-sm">
        {/* Quick Actions */}
        <div className="flex items-center justify-between mb-6 px-2">
          <button
            onClick={toggleTheme}
            className="p-2.5 rounded-xl bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-600 dark:text-surface-400 hover:text-brand-600 transition-all shadow-sm group"
          >
            {theme === 'dark' ? <Sun className="h-4 w-4 group-hover:rotate-45 transition-transform" /> : <Moon className="h-4 w-4 group-hover:-rotate-12 transition-transform" />}
          </button>
          <button className="p-2.5 rounded-xl bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-600 dark:text-surface-400 hover:text-brand-600 transition-all shadow-sm">
            <Bell className="h-4 w-4" />
          </button>
          <button
            onClick={handleLogout}
            className="p-2.5 rounded-xl bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-danger hover:bg-danger/5 transition-all shadow-sm"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>

        {/* User Card */}
        <div className="flex items-center space-x-3 p-3 bg-white dark:bg-surface-800 rounded-2xl border border-surface-200 dark:border-surface-700 shadow-sm overflow-hidden animate-slide-up">
          <div className="relative flex-shrink-0">
            <div className="h-10 w-10 rounded-xl bg-brand-500 items-center justify-center flex text-white font-bold shadow-glow-brand">
              {user?.username?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div className="absolute -bottom-1 -right-1 h-3.5 w-3.5 bg-success border-2 border-white dark:border-surface-800 rounded-full shadow-sm"></div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-surface-900 dark:text-white truncate pr-2">
              {user?.username || 'User'}
            </p>
            <p className="text-[10px] font-medium text-surface-500 dark:text-surface-500 truncate pr-2">
              {user?.email || 'Individual Plan'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
